import { del, get, keys, set, type UseStore } from 'idb-keyval';

import { DEFAULT_CACHE_MAX_AGE, DEFAULT_MAX_ITEMS, IDB_DATABASE_NAME, STORAGE_PREFIXES } from '../constants.js';
import { getActiveIdentity } from '../identity.js';
import type { CachedData, CacheMetadata, OfflineStorageConfig } from '../types.js';

import { createPortalStore } from './idbStore.js';

/**
 * Creates a namespaced key for storage.
 *
 * Keys are scoped by `portalName` AND the active identity so a portal viewed
 * as different users (impersonation, logout/login) on the same browser never
 * reads another identity's cached data.
 */
function createNamespacedKey(portalName: string, prefix: string, key: string): string {
    return `${portalName}:${getActiveIdentity()}:${prefix}:${key}`;
}

/**
 * Creates an offline storage instance for a specific portal
 */
export function createOfflineStorage(config: OfflineStorageConfig) {
    const { portalName, maxAge = DEFAULT_CACHE_MAX_AGE, maxItems = DEFAULT_MAX_ITEMS } = config;

    // Create a custom store for this portal
    const store: UseStore = createPortalStore(`${IDB_DATABASE_NAME}-${portalName}`, 'offline-cache');

    /**
     * Gets data from offline storage
     */
    async function getData<T>(key: string): Promise<CachedData<T> | null> {
        const namespacedKey = createNamespacedKey(portalName, STORAGE_PREFIXES.QUERY_CACHE, key);
        try {
            const cached = await get<CachedData<T>>(namespacedKey, store);
            if (!cached) return null;

            // Check if data has expired
            const age = Date.now() - cached.metadata.timestamp;
            if (age > maxAge) {
                await del(namespacedKey, store);
                return null;
            }

            return cached;
        } catch (error) {
            console.warn(`[OfflineStorage] Failed to get data for key "${key}":`, error);
            return null;
        }
    }

    /**
     * Sets data in offline storage with metadata
     */
    async function setData<T>(key: string, data: T, metadata?: Partial<CacheMetadata>): Promise<void> {
        const namespacedKey = createNamespacedKey(portalName, STORAGE_PREFIXES.QUERY_CACHE, key);
        const cachedData: CachedData<T> = {
            data,
            metadata: {
                timestamp: Date.now(),
                version: metadata?.version ?? 1,
                etag: metadata?.etag,
                lastModified: metadata?.lastModified,
            },
        };

        try {
            await set(namespacedKey, cachedData, store);
        } catch (error) {
            console.warn(`[OfflineStorage] Failed to set data for key "${key}":`, error);
            // If storage is full, try to clear old entries
            if (error instanceof DOMException && error.name === 'QuotaExceededError') {
                await garbageCollect();
                // Retry once
                await set(namespacedKey, cachedData, store);
            }
        }
    }

    /**
     * Deletes data from offline storage
     */
    async function deleteData(key: string): Promise<void> {
        const namespacedKey = createNamespacedKey(portalName, STORAGE_PREFIXES.QUERY_CACHE, key);
        try {
            await del(namespacedKey, store);
        } catch (error) {
            console.warn(`[OfflineStorage] Failed to delete data for key "${key}":`, error);
        }
    }

    /**
     * Gets all keys for this portal's query cache
     */
    async function getAllKeys(): Promise<string[]> {
        try {
            const allKeys = await keys<string>(store);
            const prefix = `${portalName}:${getActiveIdentity()}:${STORAGE_PREFIXES.QUERY_CACHE}:`;
            return allKeys.filter((k) => k.startsWith(prefix)).map((k) => k.slice(prefix.length));
        } catch (error) {
            console.warn('[OfflineStorage] Failed to get all keys:', error);
            return [];
        }
    }

    /**
     * Clears the current identity's cache for this portal.
     *
     * Scoped — sibling identities' entries (other users on the same browser/portal)
     * survive. Use this on sign-out or "clear cache" UX without nuking other users.
     */
    async function clearAll(): Promise<void> {
        try {
            const allKeys = await keys<string>(store);
            const prefix = `${portalName}:${getActiveIdentity()}:${STORAGE_PREFIXES.QUERY_CACHE}:`;
            const myKeys = allKeys.filter((k) => k.startsWith(prefix));
            for (const key of myKeys) {
                await del(key, store);
            }
        } catch (error) {
            console.warn('[OfflineStorage] Failed to clear storage:', error);
        }
    }

    /**
     * Removes expired entries and enforces max items limit.
     *
     * TTL eviction sweeps **all** identities for this portal so cache from
     * logged-out users gets cleaned up over time. Max-items enforcement stays
     * scoped to the current identity to avoid evicting the active user's hot
     * data because some prior identity had older entries.
     */
    async function garbageCollect(): Promise<number> {
        let removedCount = 0;
        try {
            const allKeys = await keys<string>(store);
            const portalPrefix = `${portalName}:`;
            const cachePrefix = `:${STORAGE_PREFIXES.QUERY_CACHE}:`;
            const activePrefix = `${portalName}:${getActiveIdentity()}:${STORAGE_PREFIXES.QUERY_CACHE}:`;

            const portalCacheKeys = allKeys.filter((k) => k.startsWith(portalPrefix) && k.includes(cachePrefix));

            // TTL sweep across every identity in this portal.
            const activeEntries: Array<{ key: string; timestamp: number }> = [];

            for (const key of portalCacheKeys) {
                const cached = await get<CachedData<unknown>>(key, store);
                if (!cached) continue;
                const age = Date.now() - cached.metadata.timestamp;
                if (age > maxAge) {
                    await del(key, store);
                    removedCount++;
                } else if (key.startsWith(activePrefix)) {
                    activeEntries.push({ key, timestamp: cached.metadata.timestamp });
                }
            }

            // Max-items only applies to the current identity's surviving entries.
            if (activeEntries.length > maxItems) {
                activeEntries.sort((a, b) => a.timestamp - b.timestamp);
                const toRemove = activeEntries.slice(0, activeEntries.length - maxItems);
                for (const entry of toRemove) {
                    await del(entry.key, store);
                    removedCount++;
                }
            }
        } catch (error) {
            console.warn('[OfflineStorage] Garbage collection failed:', error);
        }

        return removedCount;
    }

    /**
     * Gets metadata for a cached item
     */
    async function getMetadata(key: string): Promise<CacheMetadata | null> {
        const cached = await getData(key);
        return cached?.metadata ?? null;
    }

    /**
     * Updates only the metadata for a cached item
     */
    async function updateMetadata(key: string, metadata: Partial<CacheMetadata>): Promise<void> {
        const cached = await getData(key);
        if (cached) {
            await setData(key, cached.data, { ...cached.metadata, ...metadata });
        }
    }

    /**
     * Checks if data exists and is not expired
     */
    async function has(key: string): Promise<boolean> {
        const cached = await getData(key);
        return cached !== null;
    }

    /**
     * Gets the age of cached data in milliseconds
     */
    async function getAge(key: string): Promise<number | null> {
        const cached = await getData(key);
        if (!cached) return null;
        return Date.now() - cached.metadata.timestamp;
    }

    return {
        get: getData,
        set: setData,
        delete: deleteData,
        keys: getAllKeys,
        clear: clearAll,
        garbageCollect,
        getMetadata,
        updateMetadata,
        has,
        getAge,
        portalName,
        store,
    };
}

export type OfflineStorage = ReturnType<typeof createOfflineStorage>;
